/**
 * CabinReadinessCompare — /programs/kabin-memurlugu/interview/readiness-report/compare
 *
 * F3: Client-side diff of two scored v3 readiness reports.
 * Fetches prev (sessions[1]) + latest (sessions[0]) reports via Promise.all,
 * then computes deltas client-side. No backend compare endpoint.
 *
 * Design: Vibrant Play + Neumorphism, WCAG 2.1 AA, Framer Motion.
 * i18n: readinessReport.compare.* keys (TR + EN).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  GitCompare,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { getToken } from '../services/authToken';
import useReducedMotion from '../hooks/useReducedMotion';
import { useInterviewSimSessions } from '../hooks/cabin/useInterviewSimSessions';
import { getScoreColor } from '../lib/interviewScoreBands';
import VerdictBadge from '../components/cabin/interview/VerdictBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeader() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso, lang) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}


/**
 * Delta display config.
 * positive delta → emerald, negative → rose, zero → gray.
 * Icon + text mandatory (color alone not sufficient for a11y).
 */
function DeltaBadge({ delta, t }) {
  if (delta > 0) {
    const label = t('readinessReport.compare.delta.up').replace('{{n}}', delta);
    return (
      <span
        className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700"
        aria-label={label}
      >
        <TrendingUp size={12} aria-hidden="true" />
        {label}
      </span>
    );
  }
  if (delta < 0) {
    const label = t('readinessReport.compare.delta.down').replace('{{n}}', Math.abs(delta));
    return (
      <span
        className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-600"
        aria-label={label}
      >
        <TrendingDown size={12} aria-hidden="true" />
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-500">
      <Minus size={12} aria-hidden="true" />
      {t('readinessReport.compare.delta.same')}
    </span>
  );
}

/** Score delta summary card (CCRI or ARS) */
function ScoreDeltaCard({ label, prev, latest, t, reduced: _reduced }) {
  const delta = latest != null && prev != null ? Math.round(latest) - Math.round(prev) : null;
  const prevColor = getScoreColor(prev ?? 0);
  const latestColor = getScoreColor(latest ?? 0);

  return (
    <div
      className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-3"
      aria-label={
        delta != null
          ? `${label}: ${Math.round(prev ?? 0)}'den ${Math.round(latest ?? 0)}'ye, ${delta >= 0 ? 'artış' : 'düşüş'} ${Math.abs(delta)} puan`
          : label
      }
    >
      <p className="text-xs font-black text-gray-500 tracking-wide">{label}</p>
      <div className="flex items-end gap-3 flex-wrap">
        {/* Prev score */}
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-bold text-gray-400 mb-1">{t('readinessReport.compare.prev')}</span>
          <span className={`text-3xl font-black tabular-nums ${prevColor.tailwindText}`}>
            {prev != null ? Math.round(prev) : '—'}
          </span>
        </div>
        {/* Arrow */}
        <ChevronRight size={18} className="text-gray-300 mb-1.5" aria-hidden="true" />
        {/* Latest score */}
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-bold text-gray-400 mb-1">{t('readinessReport.compare.latest')}</span>
          <span className={`text-3xl font-black tabular-nums ${latestColor.tailwindText}`}>
            {latest != null ? Math.round(latest) : '—'}
          </span>
        </div>
        {/* Delta badge */}
        {delta != null && <DeltaBadge delta={delta} t={t} />}
      </div>
    </div>
  );
}

/** Verdict change row */
function VerdictChangeRow({ prevVerdict, latestVerdict, t }) {
  const changed = prevVerdict !== latestVerdict;

  return (
    <div className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-3">
      <p className="text-xs font-black text-gray-500 tracking-wide">{t('readinessReport.compare.verdictChange')}</p>
      <div className="flex items-center gap-3 flex-wrap">
        {prevVerdict
          ? <VerdictBadge verdict={prevVerdict} t={t} iconSize={14} variant="md" />
          : <span className="text-sm text-gray-400">—</span>}
        <ChevronRight size={16} className="text-gray-300" aria-hidden="true" />
        {latestVerdict
          ? <VerdictBadge verdict={latestVerdict} t={t} iconSize={14} variant="md" />
          : <span className="text-sm text-gray-400">—</span>}
        {!changed && (
          <span className="text-xs text-gray-400 font-semibold flex items-center gap-1">
            <Minus size={12} aria-hidden="true" />
            {t('readinessReport.compare.delta.same')}
          </span>
        )}
      </div>
    </div>
  );
}

/** Single breakdown diff row — prev bar (gray) + latest bar (color) + delta */
function BreakdownDiffRow({ areaKey, prevData, latestData, lang, reduced, t }) {
  const label = lang === 'tr'
    ? (latestData?.label_tr || prevData?.label_tr || areaKey)
    : (latestData?.label_en || prevData?.label_en || areaKey);

  const prevPct   = prevData?.pct   ?? 0;
  const latestPct = latestData?.pct ?? 0;
  const delta     = Math.round(latestPct) - Math.round(prevPct);
  const latestColor = getScoreColor(latestPct);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-gray-700 flex-1 min-w-0 truncate">{label}</span>
        <DeltaBadge delta={delta} t={t} />
      </div>
      {/* Previous bar (gray) */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-gray-400 w-12 shrink-0">{t('readinessReport.compare.prev')}</span>
        <div
          role="progressbar"
          aria-valuenow={Math.round(prevPct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} ${t('readinessReport.compare.prev')}: ${Math.round(prevPct)}%`}
          className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"
        >
          <motion.div
            className="h-full rounded-full bg-gray-300"
            initial={reduced ? false : { width: 0 }}
            animate={{ width: `${Math.min(100, Math.max(0, prevPct))}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
        <span className="text-xs text-gray-400 tabular-nums w-10 text-right shrink-0">
          {Math.round(prevPct)}%
        </span>
      </div>
      {/* Latest bar (color) */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-gray-500 w-12 shrink-0 font-semibold">{t('readinessReport.compare.latest')}</span>
        <div
          role="progressbar"
          aria-valuenow={Math.round(latestPct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} ${t('readinessReport.compare.latest')}: ${Math.round(latestPct)}%`}
          className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"
        >
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: latestColor.bar }}
            initial={reduced ? false : { width: 0 }}
            animate={{ width: `${Math.min(100, Math.max(0, latestPct))}%` }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
          />
        </div>
        <span className={`text-xs font-bold tabular-nums w-10 text-right shrink-0 ${latestColor.tailwindText}`}>
          {Math.round(latestPct)}%
        </span>
      </div>
      {/* Regression warning */}
      {delta <= -5 && (
        <div className="flex items-start gap-2 mt-1 p-2 rounded-xl bg-amber-50 border border-amber-200">
          <AlertCircle size={13} className="text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-amber-800">{t('readinessReport.compare.regression.major')}</p>
        </div>
      )}
      {delta < 0 && delta > -5 && (
        <div className="flex items-start gap-2 mt-1 p-2 rounded-xl bg-slate-50 border border-slate-200">
          <Info size={13} className="text-slate-400 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-slate-600">{t('readinessReport.compare.regression.minor')}</p>
        </div>
      )}
    </div>
  );
}

/** Collapsible accordion section for CCRI/ARS breakdown diff */
function AccordionSection({ titleKey, children, t, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors focus-visible:outline-2 focus-visible:outline-[var(--brand-primary)] focus-visible:outline-offset-2"
        aria-expanded={open}
      >
        <span className="text-sm font-black text-gray-700 tracking-wide">{t(titleKey)}</span>
        <ChevronDown
          size={16}
          className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 flex flex-col gap-4 border-t-2 border-gray-100 pt-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Back button (top-level to avoid new type on every render) ─────────────────
function BackButton({ navigate, t }) {
  return (
    <button
      type="button"
      onClick={() => navigate(-1)}
      className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-xl border-2 border-gray-200 bg-white text-gray-600 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] shadow-[1px_1px_0px_0px_rgba(0,0,0,0.06)] min-h-[44px]"
      aria-label={t('common.back')}
    >
      <ArrowLeft size={16} aria-hidden="true" />
      {t('common.back')}
    </button>
  );
}

// ── Page header (top-level, i18n eyebrow, CSS var color) ─────────────────────
function PageHeader({ navigate, t }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <BackButton navigate={navigate} t={t} />
      <div style={{ fontSize: '12.5px', fontWeight: '800', letterSpacing: '1px', color: 'var(--brand-primary)', marginBottom: '7px' }}>
        {t('readinessReport.studentPanelEyebrow')}
      </div>
      <h1
        style={{
          fontFamily: "'Fredoka','Nunito',sans-serif",
          fontWeight: '600', fontSize: '33px', margin: '0', color: '#13233F',
        }}
      >
        {t('readinessReport.compare.title')}
      </h1>
      <p style={{ margin: '7px 0 0', color: '#6B7B92', fontSize: '15px' }}>
        {t('readinessReport.compare.subtitle')}
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CabinReadinessCompare() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const reduced = useReducedMotion();

  const { sessions: allSessions, loading: sessionsLoading } = useInterviewSimSessions();

  // Explicitly sort desc by scored_at (fallback: created_at).
  // Backend /sessions orders by created_at; if an instructor scores out of creation order,
  // [0] would not be the latest scored session without this explicit sort.
  const scoredV3 = allSessions
    .filter(s => s.status === 'scored' && s.schema_version === 3)
    .sort((a, b) =>
      (b.scored_at || b.created_at || '').localeCompare(a.scored_at || a.created_at || '')
    );

  // [0] = latest (highest scored_at), [1] = previous
  const latestSession = scoredV3[0] || null;
  const prevSession   = scoredV3[1] || null;

  const [latestReport, setLatestReport] = useState(null);
  const [prevReport,   setPrevReport]   = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [fetchError,      setFetchError]    = useState(null);
  // Increment to force re-fetch (retry button)
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!latestSession || !prevSession) return;
    setLoadingReports(true);
    setFetchError(null);
    // Promise.allSettled — if one report is unavailable/deleted the other still renders;
    // both failures show an actionable error + retry button instead of an irrecoverable crash.
    Promise.allSettled([
      axios.get(
        `${API}/cabin/interview-sim/readiness-report?session_id=${encodeURIComponent(latestSession.session_id)}`,
        { headers: authHeader() }
      ),
      axios.get(
        `${API}/cabin/interview-sim/readiness-report?session_id=${encodeURIComponent(prevSession.session_id)}`,
        { headers: authHeader() }
      ),
    ]).then((results) => {
      const [latestResult, prevResult] = results;
      if (latestResult.status === 'fulfilled') setLatestReport(latestResult.value.data);
      else setLatestReport(null);
      if (prevResult.status === 'fulfilled') setPrevReport(prevResult.value.data);
      else setPrevReport(null);
      if (latestResult.status === 'rejected' || prevResult.status === 'rejected') {
        const err = latestResult.reason || prevResult.reason;
        setFetchError(
          err?.response?.data?.message_tr ||
          err?.response?.data?.detail ||
          err?.message ||
          null
        );
      }
    }).finally(() => setLoadingReports(false));
  }, [latestSession?.session_id, prevSession?.session_id, retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = sessionsLoading || loadingReports;

  // ── Empty state — no scored v3 sessions at all ─────────────────────────────
  if (!sessionsLoading && scoredV3.length === 0) {
    return (
      <div style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>
        <PageHeader navigate={navigate} t={t} />
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <div className="w-20 h-20 rounded-2xl border-2 bg-gray-100 border-gray-300 flex items-center justify-center">
            <GitCompare size={36} className="text-gray-400" aria-hidden="true" />
          </div>
          <h2 className="text-lg font-black text-[var(--text-main)]">
            {t('readinessReport.empty.no_scored_title')}
          </h2>
          <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
            {t('readinessReport.empty.no_scored_sub')}
          </p>
          <motion.button
            type="button"
            onClick={() => navigate('/programs/kabin-memurlugu/interview')}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] border-2 border-[color-mix(in_srgb,var(--brand-primary)_70%,black)] text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.12)] hover:brightness-95 transition-all min-h-[44px]"
          >
            {t('readinessReport.compare.empty.single.cta')}
          </motion.button>
        </div>
      </div>
    );
  }

  // ── Empty state — only one scored v3 session ───────────────────────────────
  if (!sessionsLoading && scoredV3.length === 1) {
    return (
      <div style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>
        <PageHeader navigate={navigate} t={t} />
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <div className="w-20 h-20 rounded-2xl border-2 bg-indigo-50 border-indigo-200 flex items-center justify-center">
            <GitCompare size={36} className="text-indigo-400" aria-hidden="true" />
          </div>
          <h2 className="text-lg font-black text-[var(--text-main)]">
            {t('readinessReport.compare.empty.single.title')}
          </h2>
          <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
            {t('readinessReport.compare.empty.single.sub')}
          </p>
          <motion.button
            type="button"
            onClick={() => navigate('/programs/kabin-memurlugu/interview')}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] border-2 border-[color-mix(in_srgb,var(--brand-primary)_70%,black)] text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.12)] hover:brightness-95 transition-all min-h-[44px]"
          >
            {t('readinessReport.compare.empty.single.cta')}
          </motion.button>
        </div>
      </div>
    );
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>
        <PageHeader navigate={navigate} t={t} />
        <div className="flex flex-col items-center gap-4 py-12">
          <Loader2 size={32} className="text-[var(--brand-primary)] animate-spin" aria-hidden="true" />
          <p className="text-sm text-gray-500">{t('readinessReport.loading')}</p>
        </div>
      </div>
    );
  }

  // ── Fetch error ────────────────────────────────────────────────────────────
  if (fetchError !== null && fetchError !== undefined) {
    return (
      <div style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>
        <PageHeader navigate={navigate} t={t} />
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <AlertCircle size={40} className="text-rose-400" aria-hidden="true" />
          <h2 className="text-lg font-black text-[var(--text-main)]">{t('readinessReport.error.title')}</h2>
          <p className="text-sm text-gray-500">
            {fetchError || t('readinessReport.compare.fetchError')}
          </p>
          <motion.button
            type="button"
            onClick={() => setRetryCount(c => c + 1)}
            whileHover={reduced ? {} : { scale: 1.03 }}
            whileTap={reduced ? {} : { scale: 0.97 }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] border-2 border-[color-mix(in_srgb,var(--brand-primary)_70%,black)] text-white font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,0.12)] hover:brightness-95 transition-all min-h-[44px]"
          >
            <RefreshCw size={16} aria-hidden="true" />
            {t('readinessReport.error.retry')}
          </motion.button>
        </div>
      </div>
    );
  }

  // ── Full compare view ──────────────────────────────────────────────────────
  if (!latestReport || !prevReport) return null;

  const prevCcri   = prevReport.scores?.ccri?.total;
  const latestCcri = latestReport.scores?.ccri?.total;
  const prevArs    = prevReport.scores?.ars?.total;
  const latestArs  = latestReport.scores?.ars?.total;

  // Merge breakdown keys from both reports
  const ccriKeys = Array.from(new Set([
    ...Object.keys(prevReport.scores?.ccri?.breakdown || {}),
    ...Object.keys(latestReport.scores?.ccri?.breakdown || {}),
  ]));
  const arsKeys = Array.from(new Set([
    ...Object.keys(prevReport.scores?.ars?.breakdown || {}),
    ...Object.keys(latestReport.scores?.ars?.breakdown || {}),
  ]));

  return (
    <div style={{ padding: '34px 40px 56px', maxWidth: 'var(--interview-content-max-w)', width: '100%' }}>
      <PageHeader navigate={navigate} t={t} />

      {/* Session date labels */}
      <div className="flex items-center gap-3 mb-5 text-xs text-gray-500">
        <span className="font-bold text-gray-400">{t('readinessReport.compare.prev')}:</span>
        <span className="font-semibold">{formatDate(prevSession?.scored_at, lang)}</span>
        <span className="text-gray-300">→</span>
        <span className="font-bold text-gray-400">{t('readinessReport.compare.latest')}:</span>
        <span className="font-semibold">{formatDate(latestSession?.scored_at, lang)}</span>
      </div>

      <div className="flex flex-col gap-5 pb-8">
        {/* ── TOP: Score deltas ── */}
        <section aria-label={t('readinessReport.section.overview')}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <ScoreDeltaCard
              label={t('readinessReport.compare.ccriLabel')}
              prev={prevCcri}
              latest={latestCcri}
              t={t}
              reduced={reduced}
            />
            <ScoreDeltaCard
              label={t('readinessReport.compare.arsLabel')}
              prev={prevArs}
              latest={latestArs}
              t={t}
              reduced={reduced}
            />
          </div>
        </section>

        {/* Verdict change */}
        <VerdictChangeRow
          prevVerdict={prevReport.verdict}
          latestVerdict={latestReport.verdict}
          t={t}
        />

        {/* ── CCRI breakdown diff ── */}
        {ccriKeys.length > 0 && (
          <AccordionSection titleKey="readinessReport.compare.ccriLabel" t={t} defaultOpen>
            {ccriKeys.map(key => (
              <BreakdownDiffRow
                key={key}
                areaKey={key}
                prevData={prevReport.scores?.ccri?.breakdown?.[key]}
                latestData={latestReport.scores?.ccri?.breakdown?.[key]}
                lang={lang}
                reduced={reduced}
                t={t}
              />
            ))}
          </AccordionSection>
        )}

        {/* ── ARS breakdown diff ── */}
        {arsKeys.length > 0 && (
          <AccordionSection titleKey="readinessReport.compare.arsLabel" t={t} defaultOpen={false}>
            {arsKeys.map(key => (
              <BreakdownDiffRow
                key={key}
                areaKey={key}
                prevData={prevReport.scores?.ars?.breakdown?.[key]}
                latestData={latestReport.scores?.ars?.breakdown?.[key]}
                lang={lang}
                reduced={reduced}
                t={t}
              />
            ))}
          </AccordionSection>
        )}
      </div>
    </div>
  );
}
